<template>
  <div v-bind="$attrs">
    <label :for="inputId">
      {{ label }}
      <v-number-input
        :id="inputId"
        :aria-label="ariaLabel"
        :model-value="value"
        type="number"
        :precision="precision"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :autofocus="autofocus"
        :step="step"
        :error="!!error"
        :error-messages="error ? [error] : undefined"
        :bg-color="readonly ? 'background' : 'surface'"
        :validate-on="isValidInternal || isPristineInternal ? 'blur' : 'input'"
        :rules="rules"
        :suffix="suffix"
        :prefix="prefix"
        :control-variant="controlVariant"
        :name="name"
        :hide-details="hideDetails"
        :clearable="clearable"
        :append-inner-icon="appendIcon"
        :min="min"
        :max="max"
        data-testid="so-number-field"
        color="success"
        variant="solo"
        density="comfortable"
        class="mb-1"
        @update:model-value="value = $event"
        @blur="$emit('blur')"
      >
        <template #append="{ isValid, isPristine }">
          {{ assignToData(isValid.value, isPristine.value) }}
          <slot name="append" />
        </template>
      </v-number-input>
    </label>
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import { ValidationRule } from "@/types/ValidationRule"

  type SoNumberInputTypeProps = {
    type: string
  }

  interface SoNumberInputTypes {
    readonly [char: string]: SoNumberInputTypeProps
  }

  const TYPES: SoNumberInputTypes = {
    number: {
      type: "number",
    },
  }

  let instanceCount = 0

  export default defineComponent({
    inheritAttrs: false,

    props: {
      modelValue: {
        type: [Number] as PropType<number | null | undefined>,
        default: 0,
      },
      label: {
        type: String,
        default: undefined,
      },
      /**
       * Accessible name for the input, for fields whose visible label sits outside the
       * component - `label` renders as visible text inside it, which is not always where
       * the label belongs. Set on the input rather than the wrapper: this component is
       * `inheritAttrs: false` and binds `$attrs` to its root div, so an `aria-label`
       * passed as an attribute lands on a div and is ignored.
       */
      ariaLabel: {
        type: String,
        default: undefined,
      },
      precision: {
        type: Number,
        default: 2,
      },
      type: {
        type: String,
        default: "number",
        validator: (val: string) => val in TYPES,
      },
      step: {
        type: [Number] as PropType<number | undefined>,
        default: 0.1,
      },
      readonly: {
        type: Boolean,
        default: false,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      error: {
        type: String,
        default: undefined,
      },
      autofocus: {
        type: Boolean,
        default: false,
      },
      placeholder: {
        type: String,
        default: undefined,
      },
      hideDetails: {
        type: [Boolean, String] as PropType<boolean | "auto">,
        default: "auto",
      },
      suffix: {
        type: String,
        default: undefined,
      },
      /**
       * Where the increment/decrement buttons sit, passed through to
       * v-number-input. 'hidden' is for narrow fields, and for fields that
       * already have another way to change the value alongside them - the
       * buttons otherwise take the room the number itself needs.
       */
      controlVariant: {
        type: String as PropType<"default" | "stacked" | "split" | "hidden">,
        default: "default",
      },
      rules: {
        type: Array as PropType<ValidationRule[]>,
        default: [] as ValidationRule[],
      },
      name: {
        type: String,
        default: undefined,
      },
      prefix: {
        type: String,
        default: undefined,
      },
      max: {
        type: Number,
        default: undefined,
      },
      min: {
        type: Number,
        default: undefined,
      },
      appendIcon: {
        type: String,
        default: undefined,
      },
      clearable: {
        type: Boolean,
        default: false,
      },
    },
    emits: ["update:modelValue", "blur"],
    data() {
      return {
        inputId: `number-input-${++instanceCount}`,
        isPristineInternal: null as null | boolean,
        isValidInternal: null as null | boolean,
      }
    },
    computed: {
      value: {
        get() {
          return Number(this.modelValue ?? 0)
        },
        set(val: number | string) {
          const num = Number.parseFloat(val as string)
          this.$emit("update:modelValue", Number.isNaN(num) ? 0 : num)
        },
      },
      getProps() {
        return TYPES[this.type]
      },
    },
    methods: {
      assignToData(isValid: boolean | null, isPristine: boolean | null) {
        this.isValidInternal = isValid
        this.isPristineInternal = isPristine
      },
    },
  })
</script>

<style scoped>
  :deep([data-lastpass-icon-root]) {
    display: none;
  }
  :deep(.v-input__append) {
    margin-inline-start: 0;
  }
  :deep(.v-field) {
    height: 48px !important;
    border-radius: var(--theme-radius);
    transition: border 250ms;
  }
  :deep(.v-field__input) {
    top: -2px;
  }
  :deep(.v-field:hover) {
    border: 1px solid rgb(var(--v-theme-on-surface));
  }
  :deep(.v-field--focused) {
    outline: var(--theme-outline);
  }
  :deep(.v-field--error) {
    border: 1px solid rgb(var(--v-theme-error)) !important;
  }
  :deep(.v-field--variant-solo) {
    box-shadow: none !important;
    border: 1px solid rgb(var(--v-theme-border));
  }
  :deep(.v-messages__message) {
    line-height: normal;
  }
</style>
